import { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../AuthContext';
import TaskCalendar from './taskcalendarbuild';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function StaffTask() {
  const [clockIns, setClockIns] = useState([]);
  const [selectedActions, setSelectedActions] = useState({}); // Row-specific actions
  const [selectedDocs, setSelectedDocs] = useState({}); // Row-specific training documents
  const [trainingDocuments, setTrainingDocuments] = useState([]);
  const [reload, setReload] = useState(false);
  const [overwrittenTasks, setOverwrittenTasks] = useState([]);
  
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const user = useContext(AuthContext);

  // Fetch clock-ins, staff, and training documents
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clockInRes, staffRes, trainingDocsRes] = await Promise.all([
          fetch('https://api.insightos.co.uk/api/clockins', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', }, credentials: 'include',
          }),
          fetch('https://api.insightos.co.uk/api/staff', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', }, credentials: 'include',
          }),
          fetch('https://api.insightos.co.uk/api/trainingdocuments', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', }, credentials: 'include',
          }),
        ]);

        if (!clockInRes.ok || !staffRes.ok || !trainingDocsRes.ok) {
          throw new Error('Failed to fetch data.');
        }

        const [clockInData, staffData, trainingDocsData] = await Promise.all([
          clockInRes.json(),
          staffRes.json(),
          trainingDocsRes.json(),
        ]);

        // Access-based assignment scope:
        // - admin can assign training to managers
        // - manager can assign training to staff
        if (user.access === 'admin' || user.access === 'manager') {
          const isTargetForCurrentUser = (staffMember) => {
            const memberAccess = String(staffMember?.Access || staffMember?.access || '').toLowerCase();

            if (user.access === 'admin') {
              return memberAccess === 'manager';
            }

            if (user.access === 'manager') {
              return memberAccess === 'staff';
            }

            return false;
          };

          const targetStaffIds = staffData.data
            .filter(isTargetForCurrentUser)
            .map((s) => s.id);

          clockInData.data = clockInData.data.filter((c) => targetStaffIds.includes(c.StaffId));
        }
        

        // Add staff email and role to each clock-in
        const clockInsWithEmail = clockInData.data.map((clockIn) => {
          const staff = staffData.data.find((s) => s.id === clockIn.StaffId);
          return { ...clockIn, Email: staff?.Email || '', StaffRole: staff?.Role || '' };
        });

        setClockIns(clockInsWithEmail);
        setTrainingDocuments(trainingDocsData.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 10000); // Poll every 10 seconds
    return () => clearInterval(intervalId);
  }, [reload]);

  useEffect(() => {
    const fetchOverwritten = async () => {
      try {
        const res = await fetch('https://api.insightos.co.uk/api/overwritten-tasks', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        setOverwrittenTasks(data.data || []);
      } catch (err) {
        console.error('Failed to fetch overwritten tasks', err);
      }
    };
    fetchOverwritten();
  }, [reload]);

  // Handle task assignment
  const handleDeleteOverwritten = async (taskId) => {
    try {
      await fetch(`https://api.insightos.co.uk/api/deletetask/${taskId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setOverwrittenTasks((prev) => prev.filter((t) => t.TaskID !== taskId));
    } catch (err) {
      console.error('Failed to delete overwritten task', err);
    }
  };

  const handleTaskAssignment = async (clockInId) => {
    const selectedAction = selectedActions[clockInId];
    const selectedDoc = selectedDocs[clockInId];
    const clockIn = clockIns.find((ci) => ci.id === clockInId);

    if (!selectedAction || !clockIn) {
      console.error('Invalid action or clockInId.');
      return;
    }

    const { StaffName, Email } = clockIn;
    const sender = user?.email || '';
    const isTraining = selectedAction === 'Training';
    const content = isTraining
      ? `Please Complete New Training: ${selectedDoc?.TrainingDocName}`
      : `Please Complete Task: ${selectedAction}`;

    const payload = {
      StaffRole: selectedAction,
      Content: content,
      StaffName,
      Training: isTraining ? 1 : 0,
      Course: isTraining ? selectedDoc?.TrainingDocName : 'None',
      sender,
      StaffEmail: Email,
    };

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/settask/${clockInId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', }, credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to assign task: ${response.statusText}`);
      }

      setReload(!reload); // Refresh data
      setSelectedActions((prev) => ({ ...prev, [clockInId]: '' }));
      setSelectedDocs((prev) => ({ ...prev, [clockInId]: null }));
    } catch (error) {
      console.error('Error assigning task:', error);
    }
  };

  const uniqueTrainingCourses = useMemo(() => {
    const byCourse = new Map();

    for (const doc of trainingDocuments) {
      const key = doc?.TrainingDocName;
      if (!key) continue;

      const pageNumber = Number(doc?.Page || 0);
      const existing = byCourse.get(key);

      if (!existing || pageNumber < Number(existing?.Page || 0)) {
        byCourse.set(key, doc);
      }
    }

    return Array.from(byCourse.values()).sort((a, b) =>
      String(a.TrainingDocName).localeCompare(String(b.TrainingDocName))
    );
  }, [trainingDocuments]);

 
  

  return (
    <div className="mt-8 flow-root mb-8" data-component="task-manager-assignment">
      {overwrittenTasks.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-400/30 bg-amber-500/10 backdrop-blur-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 shrink-0" />
            <span className="text-sm font-semibold text-amber-300">Overwritten Tasks — pushed past shift end</span>
          </div>
          <ul className="space-y-2">
            {overwrittenTasks.map((task) => (
              <li key={task.TaskID} className="flex items-start justify-between gap-3 rounded-xl border border-amber-400/20 bg-amber-500/5 px-4 py-3">
                <div className="flex-1 min-w-0 text-xs text-amber-200 space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{task.Task}</span>
                    {task.Step && <span className="text-amber-400">· Step {task.Step}</span>}
                    {task.ProcessName && <span className="text-amber-500/80">· {task.ProcessName}</span>}
                  </div>
                  <div className="flex gap-3 text-amber-400/70 flex-wrap">
                    <span>{task.Name}</span>
                    <span>{String(task.TaskDate).substring(0, 10)}</span>
                  </div>
                  {task.Description && (
                    <p className="text-amber-300/60 line-clamp-1">{task.Description}</p>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteOverwritten(task.TaskID)}
                  className="shrink-0 flex items-center justify-center h-6 w-6 rounded-lg border border-red-400/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                  title="Delete task"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="rounded-2xl border border-white/40 bg-white/65 backdrop-blur-xl shadow-xl overflow-hidden">
      <div className="-mx-2 sm:-mx-4 lg:-mx-6 overflow-x-auto">
        <div className="inline-block min-w-full py-2 align-middle px-2 sm:px-4 lg:px-6">
        <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-100/80">
              <tr>
                <th scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-xs sm:text-sm font-semibold text-slate-700 sm:pl-6"
                    >Staff Name</th>
                <th scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-xs sm:text-sm font-semibold text-slate-700 sm:pl-6"
                    >Current Role</th>
                <th scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-xs sm:text-sm font-semibold text-slate-700 sm:pl-6"
                    >Task</th>
                <th scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-xs sm:text-sm font-semibold text-slate-700 sm:pl-6"
                    >Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white/70">
            {clockIns.map((clockIn) => {
                const { id, StaffName, StaffRole } = clockIn;

                return (
                  <tr key={id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-6">
                      <div className="relative inline-block pr-3">
                        <span className="font-semibold text-slate-900">{StaffName}</span>
                        <span className="absolute -top-1 -right-0 flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-6">
                      <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 border border-indigo-100">
                        {StaffRole}
                      </span>
                    </td>
                    <td className="py-3 pl-4 pr-3 text-sm font-medium text-slate-900 sm:pl-6 min-w-[240px]">

                      <select
                        value={selectedActions[id] || ''}
                        onChange={(e) =>
                          setSelectedActions((prev) => ({ ...prev, [id]: e.target.value }))
                        }
                        data-component="task-action-select"
                        className="w-full rounded-lg border border-slate-300 bg-white/90 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      >
                        <option value="" disabled>
                          Select Task
                        </option>
                          <option value="Training">
                            Training
                          </option>
                      </select>
                      {selectedActions[id] === 'Training' && (
                        <div className="mt-2">
                          <label htmlFor={`training-${id}`} className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
                            Select Training Document
                          </label>
                          <select
                            id={`training-${id}`}
                            value={selectedDocs[id]?.TrainingDocName || ''}
                            onChange={(e) => {
                              const selectedDoc = uniqueTrainingCourses.find(
                                (doc) => doc.TrainingDocName === e.target.value
                              );
                              setSelectedDocs((prev) => ({ ...prev, [id]: selectedDoc }));
                            }}
                            data-component="task-training-doc-select"
                            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white/90 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                          >
                            <option value="" disabled>
                              Select Document
                            </option>
                            {uniqueTrainingCourses.map((doc) => (
                              <option key={doc.TrainingDocName} value={doc.TrainingDocName}>
                                {doc.TrainingDocName}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-slate-900 sm:pl-6">
                      <button
                        onClick={() => handleTaskAssignment(id)}
                        data-feature="assign-task_put"
                        className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-1.5 text-white text-sm font-semibold shadow-sm hover:from-blue-700 hover:to-indigo-700 transition-all"
                      >
                        Assign
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
}
